import { useState, useEffect, ReactNode } from "react";
import { AuthContext } from "@/auth/authcontext";
import { useUser, useRefreshToken } from "@/hooks/api/use-auth";
import type { User } from "@/hooks/api/use-auth";

type JWT = {
  access: string;
  refresh: string;
  user: User;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Only fetch user data if we have a token
  const { data: userData, error: userError } = useUser({
    enabled: !!token,
    retry: false, // Don't retry failed requests
  });

  const refreshTokenMutation = useRefreshToken();

  // Initial setup - check for stored tokens
  useEffect(() => {
    const storedToken = localStorage.getItem("accessToken");
    if (storedToken) {
      setToken(storedToken);
      // Don't set isAuthenticated until we verify the token works
    } else {
      setToken(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);

  // Handle user data fetch success
  useEffect(() => {
    if (userData) {
      setUser(userData);
      setIsAuthenticated(true);
      setIsLoading(false);
    }
  }, [userData]);

  // Handle user data fetch error
  useEffect(() => {
    if (userError?.status === 401 || userError?.status === 403) {
      // Token is invalid, try to refresh
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken && !refreshTokenMutation.isPending) {
        refreshTokenMutation.mutate(
          { refresh: refreshToken },
          {
            onSuccess: (data) => {
              localStorage.setItem("accessToken", data.access);
              setToken(data.access);
              setUser(data.user);
              setIsAuthenticated(true);
            },
            onError: () => {
              // Refresh failed, clear everything
              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
              setToken(null);
              setUser(null);
              setIsAuthenticated(false);
            },
            onSettled: () => {
              setIsLoading(false);
            },
          }
        );
      } else {
        // No refresh token or refresh in progress, clear everything
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    }
  }, [userError]);

  const handleSetUserDetails = (jwtResponse: JWT) => {
    localStorage.setItem("refreshToken", jwtResponse.refresh);
    setToken(jwtResponse.access);
    localStorage.setItem("accessToken", jwtResponse.access);
    setUser(jwtResponse.user);
    setIsLoading(false);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  };

  const contextValue = {
    token,
    user,
    isAuthenticated,
    isLoading,
    setUserDetails: handleSetUserDetails,
    logout: handleLogout,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
